/**
 * Chunked upload: every data-upload file travels in pieces, then the screen's normal endpoint
 * is called with `uploadId` instead of the file.
 *
 * nginx on the AI servers refuses any request body over 1 MB with an HTML 413 (no JSON, no
 * CORS headers, so the browser only sees "Failed to fetch"). Pieces of 512 KB never meet that
 * limit, whatever the file size:
 *
 *   POST data-upload/uploads                     {fileName, size, contentType, sha256, useCaseKey}
 *     -> {uploadId, chunkBytes, chunkCount}
 *   POST data-upload/uploads/:id/chunks/:index   multipart `chunk` (same index again replaces it)
 *   GET  data-upload/uploads/:id                 {received: number[], chunkCount, complete}
 *
 * An `uploadId` stays usable for 2 hours by several calls (Teachers: `extract-teachers` and
 * `onboarding/prefill`), so ids are cached per `File`. The routes only exist on the AI servers:
 * elsewhere callers keep sending the file itself (`isChunkedUploadAvailable`).
 */
import {dataUploadErrorCode, dataUploadErrorStatus} from 'common/helpers/dataUpload'
import type {DataUploadUseCaseKey} from 'common/helpers/dataUpload'
import {isSelectiveAiDomainHost} from 'common/helpers/selectiveAiDomain'
import {
  getChunkedDataUploadStatus,
  sendDataUploadChunk,
  startChunkedDataUpload,
} from 'setup/redux/Actions/DataUpload/dataUpload'

/** Anything `useDispatch()` returns; the actions are plain JS objects. */
type ApiDispatch = (action: any) => unknown
type Callback = (response: any) => void

/** Server default; used when the start answer leaves it out. */
export const DEFAULT_CHUNK_BYTES = 512 * 1024
/** Pieces in flight at once. */
const CHUNK_CONCURRENCY = 3
/** Attempts per piece before the upload fails. */
const CHUNK_ATTEMPTS = 3
/** The server keeps an upload for 2 hours; reuse an id for a little less than that. */
const UPLOAD_ID_TTL_MS = 110 * 60 * 1000
/** Codes that no retry can fix: the file itself was refused, or the upload is gone. */
const FINAL_CODES = new Set([
  'FILE_TYPE_NOT_ALLOWED',
  'FILE_SIGNATURE_MISMATCH',
  'TOO_LARGE',
  'EMPTY',
  'UPLOAD_NOT_FOUND',
  'FORBIDDEN',
  // The selection as a whole: one file too many, or the files together over the total.
  'TOO_MANY_FILES',
  'TOTAL_TOO_LARGE',
])

/** Whether this server takes chunked uploads (the AI servers only). */
export const isChunkedUploadAvailable = (): boolean => isSelectiveAiDomainHost()

/**
 * A saga API action as a promise: resolves with what `onSuccess` got, rejects with what
 * `onFailure` got (the envelope, or an Error when the call got no answer).
 */
export const dispatchApi = <T = any>(
  dispatch: ApiDispatch,
  build: (onSuccess: Callback, onFailure: Callback) => unknown
): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    dispatch(build(resolve, reject))
  })

export interface ChunkedUploadOptions {
  /** Lets the server refuse a type the screen can't read before any byte is sent. */
  useCaseKey?: DataUploadUseCaseKey
  /** 0..1, after each piece. */
  onProgress?: (fraction: number) => void
  /** Stops sending; the promise then rejects with an `AbortError`. */
  signal?: AbortSignal
  /**
   * The uploads already accepted in the same selection (`selectedUploadIds` on start): the
   * server then refuses this file at once with TOO_MANY_FILES (422) or TOTAL_TOO_LARGE (413)
   * when it would break the count or the total, before any piece is sent.
   */
  selectedUploadIds?: readonly string[]
}

interface UploadedFile {
  uploadId: string
  chunkBytes: number
  chunkCount: number
  expiresAt: number
}

const uploadedFiles = new WeakMap<File, UploadedFile>()

/** A file's `uploadId` while it can still be used, else null (not uploaded, or expired). */
export const uploadedIdOf = (file: File): string | null => {
  const cached = uploadedFiles.get(file)
  return cached && cached.expiresAt > Date.now() ? cached.uploadId : null
}

/** Drop a file's cached `uploadId` (a new one is made on the next upload). */
export const forgetUploadedFile = (file: File): void => {
  uploadedFiles.delete(file)
}

const abortError = () => new DOMException('Upload cancelled', 'AbortError')

export const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'AbortError'

const throwIfAborted = (signal?: AbortSignal) => {
  if (signal?.aborted) throw abortError()
}

const asPositiveInt = (value: unknown): number | null => {
  const n = Number(value)
  return Number.isInteger(n) && n > 0 ? n : null
}

/** Hex SHA-256 of the file, or undefined where Web Crypto is unavailable (plain http). */
const sha256Hex = async (file: File): Promise<string | undefined> => {
  const subtle = globalThis.crypto?.subtle
  if (!subtle) return undefined
  try {
    const digest = await subtle.digest('SHA-256', await file.arrayBuffer())
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('')
  } catch {
    return undefined
  }
}

/** A failure worth sending the piece again for: no answer, a 5xx, or a damaged piece. */
const isRetryable = (error: unknown): boolean => {
  if (isAbortError(error)) return false
  const code = dataUploadErrorCode(error)
  if (code && FINAL_CODES.has(code)) return false
  const status = dataUploadErrorStatus(error)
  return status === null || status >= 500 || status === 408 || code === 'UPLOAD_INCOMPLETE'
}

const sendPiece = async (
  dispatch: ApiDispatch,
  file: File,
  upload: UploadedFile,
  index: number,
  signal?: AbortSignal,
  onBytes?: (sentOfPiece: number) => void
): Promise<void> => {
  const blob = file.slice(index * upload.chunkBytes, (index + 1) * upload.chunkBytes)
  for (let attempt = 1; ; attempt++) {
    throwIfAborted(signal)
    onBytes?.(0)
    const form = new FormData()
    form.append('chunk', blob, `${index}.part`)
    try {
      await dispatchApi(dispatch, (ok, fail) =>
        sendDataUploadChunk(
          upload.uploadId,
          index,
          form,
          ok,
          fail,
          // The request also carries the form's boundaries: scale to the piece's own bytes.
          onBytes
            ? (loaded: number, total: number) =>
                onBytes(total > 0 ? Math.min(blob.size, (loaded / total) * blob.size) : 0)
            : undefined,
          // a piece in flight stops too, not only the ones not sent yet
          signal
        )
      )
      onBytes?.(blob.size)
      return
    } catch (error) {
      if (attempt >= CHUNK_ATTEMPTS || !isRetryable(error)) throw error
      await new Promise((resolve) => setTimeout(resolve, 400 * attempt))
    }
  }
}

/** Sends `indexes` a few at a time; the first failure stops the rest. */
const sendPieces = async (
  dispatch: ApiDispatch,
  file: File,
  upload: UploadedFile,
  indexes: readonly number[],
  options: ChunkedUploadOptions,
  alreadySent = 0
): Promise<void> => {
  let next = 0
  let failure: unknown = null
  // Progress follows the bytes actually sent: whole pieces already up, plus what each piece in
  // flight has sent so far (several go at once), over the file's size. A small file is one piece,
  // so it still moves from 0% to 100% with its own transfer instead of jumping.
  const pieceBytes = (index: number) =>
    Math.max(0, Math.min(upload.chunkBytes, file.size - index * upload.chunkBytes))
  let doneBytes = Math.min(file.size, alreadySent * upload.chunkBytes)
  const inFlight = new Map<number, number>()
  const report = () => {
    if (!options.onProgress || file.size <= 0) return
    let sending = 0
    inFlight.forEach((bytes) => {
      sending += bytes
    })
    options.onProgress(Math.min(1, (doneBytes + sending) / file.size))
  }
  const worker = async () => {
    while (failure === null && next < indexes.length) {
      const index = indexes[next++]
      try {
        await sendPiece(dispatch, file, upload, index, options.signal, (bytes) => {
          inFlight.set(index, bytes)
          report()
        })
        inFlight.delete(index)
        doneBytes += pieceBytes(index)
        report()
      } catch (error) {
        inFlight.delete(index)
        failure ??= error
      }
    }
  }
  await Promise.all(
    Array.from({length: Math.min(CHUNK_CONCURRENCY, indexes.length)}, () => worker())
  )
  if (failure !== null) throw failure
}

/**
 * Upload `file` in pieces and resolve with its `uploadId`. A file already uploaded (and not
 * expired) resolves at once with the same id. Rejects with the failing call's envelope: a
 * refusal at the start (`FILE_TYPE_NOT_ALLOWED`, `TOO_LARGE`) or at piece 0
 * (`FILE_SIGNATURE_MISMATCH`) stops before the rest is sent.
 */
export const uploadFileInPieces = async (
  dispatch: ApiDispatch,
  file: File,
  options: ChunkedUploadOptions = {}
): Promise<string> => {
  const cached = uploadedFiles.get(file)
  if (cached && cached.expiresAt > Date.now()) {
    options.onProgress?.(1)
    return cached.uploadId
  }
  throwIfAborted(options.signal)
  options.onProgress?.(0)

  const sha256 = await sha256Hex(file)
  throwIfAborted(options.signal)
  const started = await dispatchApi(dispatch, (ok, fail) =>
    startChunkedDataUpload(
      {
        fileName: file.name,
        size: file.size,
        ...(file.type ? {contentType: file.type} : {}),
        ...(sha256 ? {sha256} : {}),
        ...(options.useCaseKey ? {useCaseKey: options.useCaseKey} : {}),
        ...((options.selectedUploadIds?.length ?? 0) > 0
          ? {selectedUploadIds: [...options.selectedUploadIds!]}
          : {}),
      },
      ok,
      fail
    )
  )
  const result = started?.result ?? started
  const uploadId = result?.uploadId
  if (typeof uploadId !== 'string' || !uploadId) {
    throw {status: false, result: {code: 'UPLOAD_INCOMPLETE'}}
  }
  const chunkBytes = asPositiveInt(result.chunkBytes) ?? DEFAULT_CHUNK_BYTES
  const upload: UploadedFile = {
    uploadId,
    chunkBytes,
    chunkCount: asPositiveInt(result.chunkCount) ?? Math.max(1, Math.ceil(file.size / chunkBytes)),
    expiresAt: Date.now() + UPLOAD_ID_TTL_MS,
  }

  // Piece 0 alone first: the server checks the file's signature on it, so a renamed .exe is
  // refused about a second in, not after the whole file was sent.
  await sendPieces(dispatch, file, upload, [0], options)
  const rest = Array.from({length: upload.chunkCount - 1}, (_, i) => i + 1)
  await sendPieces(dispatch, file, upload, rest, options, 1)

  uploadedFiles.set(file, upload)
  return uploadId
}

/**
 * Re-send the pieces the server is missing (`GET data-upload/uploads/:id`), after a call
 * answered `UPLOAD_INCOMPLETE`.
 */
export const resumeUploadedFile = async (
  dispatch: ApiDispatch,
  file: File,
  options: ChunkedUploadOptions = {}
): Promise<string> => {
  const upload = uploadedFiles.get(file)
  if (!upload) return uploadFileInPieces(dispatch, file, options)
  const status = await dispatchApi(dispatch, (ok, fail) =>
    getChunkedDataUploadStatus(upload.uploadId, ok, fail)
  )
  const result = status?.result ?? status
  const received = new Set<number>(Array.isArray(result?.received) ? result.received : [])
  const missing = Array.from({length: upload.chunkCount}, (_, i) => i).filter(
    (i) => !received.has(i)
  )
  if (missing.length > 0) {
    await sendPieces(dispatch, file, upload, missing, options, upload.chunkCount - missing.length)
  }
  return upload.uploadId
}

/**
 * Callback flavour of `withUploadedFile`, for screens built on onSuccess / onFailure: posts
 * `buildForm(uploadId)` once the pieces are sent, or `buildForm(null)` (the form then carries
 * the file itself) when `chunked` is false.
 */
export const sendFileForm = (
  dispatch: ApiDispatch,
  file: File,
  options: ChunkedUploadOptions & {chunked: boolean},
  buildForm: (uploadId: string | null) => FormData,
  action: (body: FormData, onSuccess: Callback, onFailure: Callback) => unknown,
  onSuccess: Callback,
  onFailure: Callback
): void => {
  const post = (uploadId: string | null) =>
    dispatchApi(dispatch, (ok, fail) => action(buildForm(uploadId), ok, fail))
  const pending = options.chunked ? withUploadedFile(dispatch, file, options, post) : post(null)
  pending.then(onSuccess, onFailure)
}

/**
 * A step's files for ONE request, read by the server as one upload: each goes in pieces, then
 * `call` runs with every `uploadId`, in the files' order. `onProgress` is the files' average.
 * An expired or incomplete upload is sent again (every file) and `call` runs once more.
 */
export const withUploadedFiles = async <T>(
  dispatch: ApiDispatch,
  files: readonly File[],
  options: ChunkedUploadOptions,
  call: (uploadIds: string[]) => Promise<T>
): Promise<T> => {
  const upload = () => {
    const progress = files.map(() => 0)
    // Files of the selection already uploaded: a new one is checked against them on start.
    const accepted = files.map(uploadedIdOf)
    return Promise.all(
      files.map((file, index) =>
        uploadFileInPieces(dispatch, file, {
          ...options,
          selectedUploadIds: [
            ...(options.selectedUploadIds ?? []),
            ...accepted.filter((id, other): id is string => Boolean(id) && other !== index),
          ],
          onProgress: (value) => {
            progress[index] = value
            options.onProgress?.(progress.reduce((sum, one) => sum + one, 0) / files.length)
          },
        })
      )
    )
  }
  const uploadIds = await upload()
  try {
    return await call(uploadIds)
  } catch (error) {
    const code = dataUploadErrorCode(error)
    if (code !== 'UPLOAD_INCOMPLETE' && code !== 'UPLOAD_NOT_FOUND') throw error
    files.forEach(forgetUploadedFile)
    return call(await upload())
  }
}

/**
 * Upload `file` in pieces, then run `call` with its `uploadId`. When the call answers
 * `UPLOAD_INCOMPLETE` the missing pieces are sent again; when it answers `UPLOAD_NOT_FOUND`
 * (expired) the file is uploaded again. Either way `call` runs once more; a second failure is
 * the caller's to show.
 */
export const withUploadedFile = async <T>(
  dispatch: ApiDispatch,
  file: File,
  options: ChunkedUploadOptions,
  call: (uploadId: string) => Promise<T>
): Promise<T> => {
  const uploadId = await uploadFileInPieces(dispatch, file, options)
  try {
    return await call(uploadId)
  } catch (error) {
    const code = dataUploadErrorCode(error)
    if (code === 'UPLOAD_INCOMPLETE') {
      return call(await resumeUploadedFile(dispatch, file, options))
    }
    if (code === 'UPLOAD_NOT_FOUND') {
      forgetUploadedFile(file)
      return call(await uploadFileInPieces(dispatch, file, options))
    }
    throw error
  }
}
